import OrganizationService from '@forwardflow/services/OrganizationService'
import { useAppStore } from '@forwardflow/store'
import Icon from '@forwardflow/ui/Icons'
import { message, Switch } from 'antd'
import { useEffect, useState } from 'react'
import useSWR from 'swr'

const ChannelConfig = () => {
   const { currentOrganization } = useAppStore()

   const { data: orgDetails, mutate, isLoading } = useSWR(
      currentOrganization
         ? ['/organization-account/details', currentOrganization]
         : null,
      () => OrganizationService.getOrganizationDetails({ page: 1, limit: 1 })
   )

   const channelConfig = orgDetails?.deploymentConfig?.channelConfig ?? null

   const [phoneCallEnabled, setPhoneCallEnabled] = useState(true)
   const [voicemailEnabled, setVoicemailEnabled] = useState(true)
   const [smsEnabled, setSmsEnabled] = useState(true)
   const [saving, setSaving] = useState(false)

   useEffect(() => {
      if (channelConfig) {
         setPhoneCallEnabled(channelConfig.phoneCallEnabled ?? true)
         setVoicemailEnabled(channelConfig.voicemailEnabled ?? true)
         setSmsEnabled(channelConfig.smsEnabled ?? true)
      }
   }, [channelConfig])

   const handleUpdate = async (update: {
      phoneCallEnabled?: boolean
      voicemailEnabled?: boolean
      smsEnabled?: boolean
   }) => {
      setSaving(true)
      try {
         await OrganizationService.updateChannelConfig(update)
         mutate()
      } catch (error: any) {
         message.error(
            error?.response?.data?.message || 'Failed to update channel config'
         )
      } finally {
         setSaving(false)
      }
   }

   const handlePhoneCallToggle = (checked: boolean) => {
      setPhoneCallEnabled(checked)
      if (!checked) {
         setVoicemailEnabled(false)
         handleUpdate({
            phoneCallEnabled: false,
            voicemailEnabled: false,
         })
      } else {
         handleUpdate({ phoneCallEnabled: true })
      }
   }

   const handleVoicemailToggle = (checked: boolean) => {
      setVoicemailEnabled(checked)
      handleUpdate({ voicemailEnabled: checked })
   }

   const handleSmsToggle = (checked: boolean) => {
      setSmsEnabled(checked)
      handleUpdate({ smsEnabled: checked })
   }

   const enabledCount =
      Number(phoneCallEnabled) +
      Number(phoneCallEnabled && voicemailEnabled) +
      Number(smsEnabled)

   if (isLoading) {
      return (
         <div className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-white/[0.035] p-6">
            <div className="h-5 w-48 animate-pulse rounded bg-white/10" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-white/[0.07]" />
            <div className="h-16 animate-pulse rounded-xl bg-white/[0.05]" />
            <div className="h-16 animate-pulse rounded-xl bg-white/[0.05]" />
         </div>
      )
   }

   return (
      <section className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.035] shadow-[0_18px_50px_rgba(0,0,0,0.18)]">
         <div className="flex flex-col gap-3 border-b border-white/10 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
            <div className="flex items-start gap-4">
               <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/20">
                  <Icon name="Message" className="h-5 w-5 fill-primary-light" />
               </span>
               <div>
                  <h2 className="font-medium text-white">Communication channels</h2>
                  <p className="mt-1 max-w-xl text-sm leading-6 text-white/60">
                     Choose which channels campaigns can use. Disabled channels are skipped automatically during execution.
                  </p>
               </div>
            </div>
            <span className="ml-[60px] shrink-0 text-xs text-white/45 sm:ml-0 sm:pt-1">
               {saving ? 'Saving changes…' : `${enabledCount} of 3 enabled`}
            </span>
         </div>

         <div className="space-y-2 p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.08] bg-black/10 px-4 py-3.5">
               <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.07]">
                     <Icon name="Telephone" className="h-5 w-5 fill-white/70" />
                  </span>
                  <div className="min-w-0">
                     <p className="text-sm font-medium text-white">Phone calls</p>
                     <p className="mt-0.5 text-xs leading-5 text-white/45">
                        Allow campaigns to place outbound calls to leads.
                     </p>
                  </div>
               </div>
               <Switch
                  checked={phoneCallEnabled}
                  onChange={handlePhoneCallToggle}
                  disabled={saving}
                  aria-label="Enable outbound phone calls"
                  className="shrink-0 bg-white/20"
               />
            </div>

            <div
               className={`flex items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition sm:ml-12 ${
                  phoneCallEnabled
                     ? 'border-white/[0.08] bg-black/10'
                     : 'border-white/[0.05] bg-white/[0.02] opacity-50'
               }`}
            >
               <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.07]">
                     <Icon name="Telephone" className="h-5 w-5 fill-white/70" />
                  </span>
                  <div className="min-w-0">
                     <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-white">Voicemail</p>
                        <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-[10px] text-white/45">
                           Requires phone calls
                        </span>
                     </div>
                     <p className="mt-0.5 text-xs leading-5 text-white/45">
                        Leave a voicemail when an outbound call is unanswered.
                     </p>
                  </div>
               </div>
               <Switch
                  checked={phoneCallEnabled && voicemailEnabled}
                  onChange={handleVoicemailToggle}
                  disabled={saving || !phoneCallEnabled}
                  aria-label="Enable voicemail for unanswered calls"
                  className="shrink-0 bg-white/20"
               />
            </div>

            <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.08] bg-black/10 px-4 py-3.5">
               <div className="flex min-w-0 items-center gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.07]">
                     <Icon name="Message" className="h-5 w-5 fill-white/70" />
                  </span>
                  <div className="min-w-0">
                     <p className="text-sm font-medium text-white">
                        SMS / text messages
                     </p>
                     <p className="mt-0.5 text-xs leading-5 text-white/45">
                        Allow campaigns to send text messages to leads.
                     </p>
                  </div>
               </div>
               <Switch
                  checked={smsEnabled}
                  onChange={handleSmsToggle}
                  disabled={saving}
                  aria-label="Enable SMS and text messages"
                  className="shrink-0 bg-white/20"
               />
            </div>
         </div>
      </section>
   )
}

export default ChannelConfig
